import { type FC, useState, useRef, useMemo, useEffect } from 'react';

import { ModalActions, ModalContainer, ModalHeader } from '@/utils/modal';
import { successToast, errorToast } from '@/helpers';
import { ApiService } from '@/support/ApiService.ts';
import InVivoError from '@/model/InVivoError.ts';
import { ExceptionHandler } from '@/utils/ExceptionHandler.ts';
import { _notNil, _isEmptyString, _isNil, _isNotBlank } from '@/littledash';
import type { HotTableRef } from '@/components/UI/SpreadSheet/HotTable';
import { type ValidationData, type ObservationTypeRow, parseScoringScale } from '@/utils/observationTypeValidation';

import BulkAddObservationTypesTable from './BulkAddObservationTypesTable';

interface BulkAddObservationTypesProps {
  onSave: () => void;
  onCancel: () => void;
}

const BulkAddObservationTypes: FC<BulkAddObservationTypesProps> = ({ onSave, onCancel }) => {
  const [saving, setSaving] = useState(false);
  const [validationData, setValidationData] = useState<ValidationData | null>(null);
  const [loadingValidation, setLoadingValidation] = useState(true);
  const [hasValidationErrors, setHasValidationErrors] = useState(false);
  const ref = useRef<HotTableRef>(null);

  function getHotInstance(): { getData: () => Array<ObservationTypeRow> } | null {
    const instance = ref.current?.hotInstance;
    if (_isNil(instance)) {
      return null;
    }
    return {
      getData: () => instance.getData() as Array<ObservationTypeRow>,
    };
  }

  useEffect(() => {
    const fetchValidationData = async (): Promise<void> => {
      try {
        const response = await ApiService.call({
          endpoint: 'GET /api/v1/team-glossary/observation-validation-data',
          path: undefined,
          options: {
            onError: { toast: false, throw: true, capture: false, slug: 'bulk-add-observation-types-validation-load' },
          },
        });
        setValidationData(response.body as ValidationData);
      } catch (error) {
        errorToast('Failed to load validation data');
        ExceptionHandler.captureException(
          new InVivoError('Failed to load validation data', {
            cause: error,
            slug: 'bulk-add-observation-types-validation-load',
          })
        );
        setValidationData(null);
      } finally {
        setLoadingValidation(false);
      }
    };

    void fetchValidationData();
  }, []);

  const initialData: Array<Array<string>> = useMemo(() => [['', '', '', ''], []], []);

  async function handleSave(): Promise<void> {
    const hotInstance = getHotInstance();
    if (_isNil(hotInstance)) {
      errorToast('Unable to access table data');
      return;
    }

    setSaving(true);

    try {
      const currentData = hotInstance.getData();

      const validRows = currentData.filter((row) => {
        const [title] = row;
        return _isNotBlank(title);
      });

      if (validRows.length === 0) {
        errorToast('Please add at least one observation type');
        setSaving(false);
        return;
      }

      const teamGlossariesPayload = validRows.map((row) => {
        const [title, label, scoringScale, gradingInfo] = row;

        const maybeTitle = (_notNil(title) ? title.toString() : '').trim();
        const maybeLabel = (_notNil(label) ? label.toString() : '').trim();
        const maybeGradingInfo = (_notNil(gradingInfo) ? gradingInfo.toString() : '').trim();

        const scale = parseScoringScale(scoringScale as string);
        let type: 'none' | 'numeric' | 'severity' = 'none';
        let options: { min: string; max: string } | null = null;

        if (_notNil(scale)) {
          if (scale.type === 'numeric') {
            type = 'numeric';
            options = {
              min: scale.minValue.toString(),
              max: scale.maxValue.toString(),
            };
          } else if (scale.type === 'severity') {
            type = 'severity';
          }
        }

        return {
          title: maybeTitle,
          type,
          description: !_isEmptyString(maybeGradingInfo) ? maybeGradingInfo : null,
          group: 'observations' as const,
          ...(_notNil(options) && { options }),
          ...(!_isEmptyString(maybeLabel) && { label: maybeLabel }),
        };
      });

      await ApiService.call({
        endpoint: 'POST /api/v1/team-glossary/observations',
        path: undefined,
        body: {
          team_glossaries: teamGlossariesPayload,
        },
        options: { onError: { toast: false, throw: true, capture: false, slug: 'bulk-add-observation-types-save' } },
      });

      successToast(`Successfully added ${validRows.length} observation type${validRows.length !== 1 ? 's' : ''}`);
      onSave();
    } catch (error) {
      errorToast('Failed to create observation types');
      ExceptionHandler.captureException(
        new InVivoError('Failed to create observation types', {
          cause: error,
          slug: 'bulk-add-observation-types-save',
        })
      );
    } finally {
      setSaving(false);
    }
  }

  return (
    <ModalContainer className="bg-white" data-testid="bulk-add-observation-types-modal">
      <ModalHeader
        title="Create observation types"
        closeModal={onCancel}
        closeDisabled={saving}
        className="pa3 bb b--moon-gray w-100"
      />

      <div className="pa3">
        {loadingValidation ? (
          <div className="tc pa4" data-testid="loading-validation-data">
            <div className="mb2">Loading validation data...</div>
          </div>
        ) : (
          <BulkAddObservationTypesTable
            data={initialData}
            innerRef={ref}
            validationData={validationData}
            setInvalid={setHasValidationErrors}
          />
        )}
      </div>

      <ModalActions
        className="pa3 bt b--moon-gray w-100"
        onSubmit={handleSave}
        onCancel={onCancel}
        submitBtnText="Create"
        cancelBtnText="Cancel"
        submitButtonProps={{
          loading: saving,
          disabled: saving || loadingValidation || hasValidationErrors,
          'data-testid': 'create-observation-types-submit',
        }}
        cancelButtonProps={{
          disabled: saving,
          'data-testid': 'create-observation-types-cancel',
        }}
      />
    </ModalContainer>
  );
};

export default BulkAddObservationTypes;
